import React, { useState, useEffect } from 'react';
import { getYear, getMonth, getDate, getDaysInMonth } from 'date-fns';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { fetchBudgetsWithTransactions } from '../api/budgets';
import LoadingSpinner from '../components/LoadingSpinner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const currentYear = getYear(new Date());
const currentMonth = getMonth(new Date());
const years = [currentYear, ...Array.from({ length: 10 }, (_, i) => currentYear - i - 1)];
const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function NativePillSelect({ 
  value, 
  options, 
  onChange, 
  className = "" 
}: { 
  value: string; 
  options: string[]; 
  onChange: (v: string) => void;
  className?: string;
}) {
  return (
    <div className={`relative flex-1 ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none bg-gray-200 rounded-full px-6 py-2.5 text-base font-semibold text-gray-800 focus:outline-none select-none text-center cursor-pointer"
        style={{
          backgroundImage: 'none',
          paddingRight: '2.5rem',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          touchAction: 'manipulation'
        }}
      >
        {options.map(option => (
          <option key={option} value={option} className="text-left py-2 px-4">
            {option}
          </option>
        ))}
      </select>
      <ChevronDownIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
    </div>
  );
}

function QuickStatsCard({ title, value, subtitle, color = "gray" }: { 
  title: string; 
  value: string; 
  subtitle?: string;
  color?: "gray" | "green" | "red" | "blue" | "yellow";
}) {
  const colorClasses = {
    gray: "bg-white-50 border-gray-200",
    green: "bg-green-50 border-green-200",
    red: "bg-red-50 border-red-200", 
    blue: "bg-blue-50 border-blue-200",
    yellow: "bg-yellow-50 border-yellow-200"
  };

  const textColors = {
    gray: "text-gray-900",
    green: "text-green-900",
    red: "text-red-900",
    blue: "text-blue-900",
    yellow: "text-yellow-900"
  };

  return (
    <div className={`flex-1 bg-white rounded-xl p-4 border ${colorClasses[color]} shadow-sm`}>
      <div className="text-center">
        <div className={`text-2xl font-bold ${textColors[color]} mb-1`}>
          {value}
        </div>
        <div className="text-sm font-medium text-gray-600 mb-1">
          {title}
        </div>
        {subtitle && (
          <div className="text-xs text-gray-500">
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
}

function MTDAnalysisItem({ analysis }: { analysis: MTDAnalysis }) {
  // Render icon - handle both Font Awesome classes and emoji
  const renderIcon = () => {
    if (analysis.budgetIcon.startsWith('fas fa-') || analysis.budgetIcon.startsWith('far fa-') || analysis.budgetIcon.startsWith('fab fa-')) {
      return <i className={analysis.budgetIcon}></i>;
    }
    return analysis.budgetIcon;
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#F8F9FA]">
            <div className="text-base">{renderIcon()}</div>
          </div>
          <div>
            <div className="font-semibold text-lg text-gray-900">{analysis.budgetName}</div>
            <div className="text-base text-gray-600 font-semibold">
              ${analysis.currentMonthSpent.toFixed(2)} vs ${analysis.previousMonthSpent.toFixed(2)} last month
            </div>
          </div>
        </div>
        <div className={`text-lg font-semibold ${analysis.isIncrease ? 'text-red-600' : 'text-green-600'}`}>
          {analysis.isIncrease ? '+' : ''}{analysis.percentageChange.toFixed(0)}%
        </div>
      </div>
    </div>
  );
}

function BudgetStatusItem({ budget }: { budget: BudgetStatus }) {
  // Render icon - handle both Font Awesome classes and emoji
  const renderIcon = () => {
    if (budget.budgetIcon.startsWith('fas fa-') || budget.budgetIcon.startsWith('far fa-') || budget.budgetIcon.startsWith('fab fa-')) {
      return <i className={budget.budgetIcon}></i>;
    }
    return budget.budgetIcon;
  };

  const getStatusColor = () => {
    if (budget.isUnderBudget) {
      return budget.percentageUsed <= 95 ? 'text-green-600' : 'text-yellow-600';
    }
    return 'text-red-600';
  };

  const getStatusText = () => {
    if (budget.isUnderBudget) {
      const remaining = budget.amount - budget.spent;
      return `$${remaining.toFixed(2)} remaining`;
    }
    const over = budget.spent - budget.amount;
    return `$${over.toFixed(2)} over budget`;
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#F8F9FA]">
            <div className="text-base">{renderIcon()}</div>
          </div>
          <div>
            <div className="font-semibold text-lg text-gray-900">{budget.budgetName}</div>
            <div className="text-base text-gray-600">
              ${budget.spent.toFixed(2)} of ${budget.amount.toFixed(2)}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-lg font-semibold ${getStatusColor()}`}>
            {budget.percentageUsed.toFixed(0)}%
          </div>
          <div className="text-sm text-gray-500">
            {getStatusText()}
          </div>
        </div>
      </div>
    </div>
  );
}

interface MTDAnalysis {
  budgetId: string;
  budgetName: string;
  budgetIcon: string;
  currentMonthSpent: number;
  previousMonthSpent: number;
  percentageChange: number;
  isIncrease: boolean;
}

interface QuickStats {
  totalSpent: number;
  averageDailySpending: number;
  totalBudget: number;
  budgetUtilization: number;
  daysInMonth: number;
  daysElapsed: number;
}

interface BudgetStatus {
  budgetId: string;
  budgetName: string;
  budgetIcon: string;
  amount: number;
  spent: number;
  percentageUsed: number;
  isUnderBudget: boolean;
  isOverBudget: boolean;
}

export function Analyze() {
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mtdAnalysis, setMtdAnalysis] = useState<MTDAnalysis[]>([]);
  const [quickStats, setQuickStats] = useState<QuickStats | null>(null);
  const [showMTDInfo, setShowMTDInfo] = useState(false);
  const [showHealthScoreInfo, setShowHealthScoreInfo] = useState(false);
  const [currentMonthBudgets, setCurrentMonthBudgets] = useState<any[]>([]);
  const [budgetStatuses, setBudgetStatuses] = useState<{
    underBudget: BudgetStatus[];
    overBudget: BudgetStatus[];
    canShowData: boolean;
  }>({ underBudget: [], overBudget: [], canShowData: false });
  const [topBudgets, setTopBudgets] = useState<Array<{
    id: string;
    name: string;
    icon: string;
    spent: number;
    amount: number;
  }>>([]);
  const [spendingVelocity, setSpendingVelocity] = useState<any>(null);
  const [budgetHealthScore, setBudgetHealthScore] = useState<{
    score: number;
    stage: string;
    canCalculate: boolean;
  } | null>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [optimizationOpportunities, setOptimizationOpportunities] = useState<{
    opportunities: Array<{
      budgetId: string;
      budgetName: string;
      currentAmount: number;
      suggestedAmount: number;
      reductionAmount: number;
      currentUtilization: number;
      previousUtilization: number;
    }>;
    totalSavings: number;
    canShowData: boolean;
  }>({ opportunities: [], totalSavings: 0, canShowData: false });

  // Calculate month-to-date percentage
  const calculateMTDPercentage = () => {
    const today = new Date();
    const currentDay = getDate(today);
    const daysInMonth = getDaysInMonth(today);
    return (currentDay / daysInMonth) * 100;
  };

  // Calculate optimization opportunities
  const calculateOptimizationOpportunities = (currentBudgets: any[], previousBudgets: any[]) => {
    const opportunities: Array<{
      budgetId: string;
      budgetName: string;
      currentAmount: number;
      suggestedAmount: number;
      reductionAmount: number;
      currentUtilization: number;
      previousUtilization: number;
    }> = [];

    let totalSavings = 0;

    currentBudgets.forEach(currentBudget => {
      const previousBudget = previousBudgets.find(b => b._id === currentBudget._id);
      
      if (!previousBudget) return;

      const currentSpent = currentBudget.spent || 0;
      const currentAmount = currentBudget.amount || 0;
      const previousSpent = previousBudget.spent || 0;
      const previousAmount = previousBudget.amount || 0;

      // Only consider budgets with meaningful amounts
      if (currentAmount < 50) return;

      const currentUtilization = currentAmount > 0 ? (currentSpent / currentAmount) * 100 : 0;
      const previousUtilization = previousAmount > 0 ? (previousSpent / previousAmount) * 100 : 0;

      // Look for consistently underutilized budgets (both months < 70% utilization)
      if (currentUtilization < 70 && previousUtilization < 70) {
        // Calculate suggested reduction (reduce by 20% of current budget)
        const suggestedReduction = currentAmount * 0.2;
        const suggestedAmount = currentAmount - suggestedReduction;
        
        // Only suggest if the reduction is meaningful (> $10)
        if (suggestedReduction > 10) {
          opportunities.push({
            budgetId: currentBudget._id,
            budgetName: currentBudget.name,
            currentAmount,
            suggestedAmount,
            reductionAmount: suggestedReduction,
            currentUtilization,
            previousUtilization
          });
          
          totalSavings += suggestedReduction;
        }
      }
    });

    // Sort by reduction amount (highest first)
    opportunities.sort((a, b) => b.reductionAmount - a.reductionAmount);

    return {
      opportunities,
      totalSavings,
      canShowData: opportunities.length > 0
    };
  };

  // Calculate Budget Health Score
  const calculateBudgetHealthScore = (budgets: any[]): { score: number; stage: string; canCalculate: boolean } => {
    if (budgets.length === 0) return { score: 0, stage: 'insufficient-data', canCalculate: false };

    // Calculate month progress percentage
    const now = new Date();
    const currentYearMonth = now.getFullYear() * 12 + now.getMonth();
    const viewedYearMonth = year * 12 + month;
    const isCurrentMonth = viewedYearMonth === currentYearMonth;

    const daysInMonth = getDaysInMonth(new Date(year, month));
    const daysElapsed = isCurrentMonth ? Math.min(getDate(new Date()), daysInMonth) : daysInMonth;
    const monthProgressPercent = (daysElapsed / daysInMonth) * 100;

    // Calculate health scores for each budget relative to month progress
    let totalHealthScore = 0;
    let validBudgets = 0;
    let stage = 'assessment';

    budgets.forEach(budget => {
      const spent = budget.spent || 0;
      const amount = budget.amount || 0;

      if (amount <= 0) return; // Skip budgets with no allocation

      const utilizationPercent = (spent / amount) * 100;

      // Calculate individual budget health score - start with 100 and subtract penalties
      let budgetHealthScore = 100;

      // Penalty for overspending (only if over 100% of budget)
      if (utilizationPercent > 100) {
        const overSpendPercent = utilizationPercent - 100;
        // Steep penalty for overspending - 2 points per percentage over budget
        const overspendPenalty = overSpendPercent * 2;
        budgetHealthScore -= overspendPenalty;
      }

      // Penalty for being too conservative in mid/late month
      if (monthProgressPercent >= 50 && utilizationPercent < 30) {
        // Mid/late month with very low utilization - penalty
        const conservativePenalty = (30 - utilizationPercent) * 1; // 1 point per percentage under 30%
        budgetHealthScore -= conservativePenalty;
      } else if (monthProgressPercent >= 75 && utilizationPercent < 50) {
        // Late month with low utilization - additional penalty
        const conservativePenalty = (50 - utilizationPercent) * 0.5; // 0.5 points per percentage under 50%
        budgetHealthScore -= conservativePenalty;
      }

      // Ensure score doesn't go below 0
      budgetHealthScore = Math.max(0, budgetHealthScore);

      totalHealthScore += budgetHealthScore;
      validBudgets++;
    });

    if (validBudgets === 0) {
      return { score: 0, stage: 'no-valid-budgets', canCalculate: false };
    }

    // Calculate weighted average with penalty for overspent budgets
    const averageHealthScore = totalHealthScore / validBudgets;

    // Find the worst performing budget score
    let worstBudgetScore = 100;
    budgets.forEach(budget => {
      const spent = budget.spent || 0;
      const amount = budget.amount || 0;

      if (amount > 0) {
        const utilizationPercent = (spent / amount) * 100;
        
        // Use the same penalty-based logic as the main algorithm
        let budgetScore = 100;

        // Penalty for overspending (only if over 100% of budget)
        if (utilizationPercent > 100) {
          const overSpendPercent = utilizationPercent - 100;
          // Steep penalty for overspending - 2 points per percentage over budget
          const overspendPenalty = overSpendPercent * 2;
          budgetScore -= overspendPenalty;
        }

        // Penalty for being too conservative in mid/late month
        if (monthProgressPercent >= 50 && utilizationPercent < 30) {
          // Mid/late month with very low utilization - penalty
          const conservativePenalty = (30 - utilizationPercent) * 1; // 1 point per percentage under 30%
          budgetScore -= conservativePenalty;
        } else if (monthProgressPercent >= 75 && utilizationPercent < 50) {
          // Late month with low utilization - additional penalty
          const conservativePenalty = (50 - utilizationPercent) * 0.5; // 0.5 points per percentage under 50%
          budgetScore -= conservativePenalty;
        }

        // Ensure score doesn't go below 0
        budgetScore = Math.max(0, budgetScore);

        if (budgetScore < worstBudgetScore) {
          worstBudgetScore = budgetScore;
        }
      }
    });

    // Apply penalty based on worst performing budget
    // If worst budget is very low, heavily penalize the overall score
    let finalScore = averageHealthScore;

    if (worstBudgetScore <= 20) {
      // Severe overspending penalty - heavily weight the worst budget
      finalScore = (averageHealthScore * 0.3) + (worstBudgetScore * 0.7);
    } else if (worstBudgetScore <= 40) {
      // Moderate overspending penalty
      finalScore = (averageHealthScore * 0.5) + (worstBudgetScore * 0.5);
    } else if (worstBudgetScore <= 60) {
      // Light overspending penalty
      finalScore = (averageHealthScore * 0.7) + (worstBudgetScore * 0.3);
    }

    return {
      score: Math.round(Math.max(0, Math.min(100, finalScore))),
      stage,
      canCalculate: true
    };
  };

  // Analyze budgets for detailed scoring explanation
  const analyzeBudgetsForExplanation = (budgets: any[]) => {
    const now = new Date();
    const currentYearMonth = now.getFullYear() * 12 + now.getMonth();
    const viewedYearMonth = year * 12 + month;
    const isCurrentMonth = viewedYearMonth === currentYearMonth;

    const daysInMonth = getDaysInMonth(new Date(year, month));
    const daysElapsed = isCurrentMonth ? Math.min(getDate(new Date()), daysInMonth) : daysInMonth;
    const monthProgressPercent = (daysElapsed / daysInMonth) * 100;

    let overspentBudgets: Array<{name: string, overSpendPercent: number, penalty: number}> = [];
    let conservativeBudgets: Array<{name: string, utilizationPercent: number, penalty: number}> = [];
    let worstBudget = '';
    let worstBudgetScore = 100;
    let budgetCount = 0;
    let perfectBudgets = 0;

    budgets.forEach(budget => {
      const spent = budget.spent || 0;
      const amount = budget.amount || 0;

      if (amount > 0) {
        const utilizationPercent = (spent / amount) * 100;
        budgetCount++;

        // Calculate budget score using the same penalty-based logic
        let budgetScore = 100;

        // Penalty for overspending
        if (utilizationPercent > 100) {
          const overSpendPercent = utilizationPercent - 100;
          const overspendPenalty = overSpendPercent * 2;
          budgetScore -= overspendPenalty;
          overspentBudgets.push({
            name: budget.name,
            overSpendPercent: Math.round(overSpendPercent),
            penalty: Math.round(overspendPenalty)
          });
        } else {
          perfectBudgets++;
        }

        // Penalty for being too conservative
        if (monthProgressPercent >= 50 && utilizationPercent < 30) {
          const conservativePenalty = (30 - utilizationPercent) * 1;
          budgetScore -= conservativePenalty;
          conservativeBudgets.push({
            name: budget.name,
            utilizationPercent: Math.round(utilizationPercent),
            penalty: Math.round(conservativePenalty)
          });
        } else if (monthProgressPercent >= 75 && utilizationPercent < 50) {
          const conservativePenalty = (50 - utilizationPercent) * 0.5;
          budgetScore -= conservativePenalty;
          conservativeBudgets.push({
            name: budget.name,
            utilizationPercent: Math.round(utilizationPercent),
            penalty: Math.round(conservativePenalty)
          });
        }

        budgetScore = Math.max(0, budgetScore);

        if (budgetScore < worstBudgetScore) {
          worstBudgetScore = budgetScore;
          worstBudget = budget.name;
        }
      }
    });

    return {
      overspentBudgets,
      conservativeBudgets,
      worstBudget,
      worstBudgetScore,
      monthProgressPercent,
      daysElapsed,
      perfectBudgets,
      budgetCount
    };
  };

  // Generate explanation for the current health score
  const generateHealthScoreExplanation = (budgets: any[], score: number): string => {
    const analysis = analyzeBudgetsForExplanation(budgets);
    const { overspentBudgets, conservativeBudgets, worstBudget, worstBudgetScore, monthProgressPercent, daysElapsed, perfectBudgets, budgetCount } = analysis;

    // 1) Headline statement based on score
    let explanation = '';
    if (score >= 90) explanation = 'Your budget health is excellent. ';
    else if (score >= 80) explanation = 'Your budget health is very good. ';
    else if (score >= 70) explanation = 'Your budget health is good with room for improvement. ';
    else if (score >= 50) explanation = 'Your budget health needs attention. ';
    else if (score >= 30) explanation = 'Your budget health has significant issues. ';
    else explanation = 'Your budget health requires immediate attention. ';

    // Helper to join phrases with commas and 'and'
    const joinPhrases = (items: string[]) => {
      if (items.length === 0) return '';
      if (items.length === 1) return items[0];
      if (items.length === 2) return `${items[0]}, and ${items[1]}`;
      return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
    };

    // 2) Build main drivers (negatives) and strengths (positives)
    const negatives: string[] = [];
    const positives: string[] = [];

    // Overspending (primary driver)
    if (overspentBudgets.length > 0) {
      if (overspentBudgets.length === 1) {
        const b = overspentBudgets[0];
        negatives.push(`${b.name} is ${b.overSpendPercent}% over budget`);
      } else {
        const worstOver = overspentBudgets.reduce((worst, current) =>
          current.overSpendPercent > worst.overSpendPercent ? current : worst
        );
        negatives.push(`${overspentBudgets.length} categories are over budget; the worst being ${worstOver.name} at ${worstOver.overSpendPercent}% over`);
      }
    }

    // Conservative usage (secondary driver) only when relevant by time-in-month
    if (conservativeBudgets.length > 0) {
      const timing = monthProgressPercent >= 75 ? 'this late in the month' : 'mid‑month';
      if (conservativeBudgets.length === 1) {
        const b = conservativeBudgets[0];
        negatives.push(`${b.name} has seen little activity (${b.utilizationPercent}%) ${timing}`);
      } else {
        negatives.push(`${conservativeBudgets.length} categories have seen little activity ${timing}`);
      }
    }

    // Worst single budget impact
    if (worstBudget && worstBudgetScore <= 50) {
      negatives.push(`the heavy overspending in ${worstBudget} is pulling the score down significantly`);
    }

    // Positives: only surface when they help context and won't read as contradictory
    if (perfectBudgets > 0 && perfectBudgets < budgetCount) {
      if (score >= 60 || overspentBudgets.length === 0) {
        positives.push('most categories stayed within budget');
      }
    }

    // 3) Compose narrative: issues first, then strengths (or "while" clause for low scores)
    if (negatives.length > 0) {
      const drivers = joinPhrases(negatives);
      if (score < 50) explanation += `${drivers}. `;
      else explanation += `This is mostly because ${drivers}. `;
    }
    if (positives.length > 0) {
      const strengths = joinPhrases(positives);
      if (score < 50) explanation += `On the plus side, ${strengths}. `;
      if (score < 75) explanation += `Despite that, ${strengths}. `;
      else explanation += `You’re also doing well in that ${strengths}. `;
    }

    // 4) Month context and recommendation
    if (monthProgressPercent <= 25) {
      explanation += `With only ${daysElapsed} days completed (${monthProgressPercent.toFixed(1)}% of the month), `;
      if (score >= 90) explanation += 'you’re off to an excellent start.';
      else if (score >= 70) explanation += 'you’re building a solid foundation.';
      else explanation += 'a few early adjustments can help you stay on track.';
    } else if (monthProgressPercent <= 50) {
      explanation += `At ${monthProgressPercent.toFixed(1)}% of the month, `;
      if (score >= 80) explanation += 'you’re maintaining strong control.';
      else if (score >= 60) explanation += 'you’re doing well with room to optimize.';
      else explanation += 'shifting focus to the biggest drivers will improve the outlook.';
    } else if (monthProgressPercent < 100) {
      explanation += `As you approach month‑end (${monthProgressPercent.toFixed(1)}% complete), `;
      if (score >= 80) explanation += 'you’re finishing strong.';
      else if (score >= 60) explanation += 'you’re on track with a few areas to improve.';
      else explanation += 'prioritizing the biggest issues now will help you finish stronger.';
    } else {
      explanation += 'Since this month is complete, consider using this breakdown to rebalance allocations for the future.';
    }

    return explanation;
  };

  // Calculate spending velocity metrics
  const calculateSpendingVelocity = async (currentBudgets: any[], previousBudgets: any[], year: number, month: number) => {
    const totalSpent = currentBudgets.reduce((sum, budget) => sum + (budget.spent || 0), 0);
    const totalBudget = currentBudgets.reduce((sum, budget) => sum + (budget.amount || 0), 0);
    const daysInMonth = getDaysInMonth(new Date(year, month));

    // Check if the month being viewed is in the past
    const now = new Date();
    const currentYearMonth = now.getFullYear() * 12 + now.getMonth();
    const viewedYearMonth = year * 12 + month;
    const isPastMonth = viewedYearMonth < currentYearMonth;

    // Calculate daysElapsed for both current and previous months
    const daysElapsed = isPastMonth ? daysInMonth : Math.min(getDate(new Date()), daysInMonth);

    // Calculate burn rate based on whether we're viewing current or previous month
    const burnRate = daysElapsed > 0 ? totalSpent / daysElapsed : 0;

    // Previous month burn rate for comparison - use total days in previous month
    const previousMonthTotal = previousBudgets.reduce((sum, budget) => sum + (budget.spent || 0), 0);
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const previousMonthDays = getDaysInMonth(new Date(prevYear, prevMonth));
    const previousBurnRate = previousMonthDays > 0 ? previousMonthTotal / previousMonthDays : 0;

    // Acceleration: percentage change from previous month
    const acceleration = previousBurnRate > 0 ? ((burnRate - previousBurnRate) / previousBurnRate) * 100 : 0;

    // For past months, show N/A for predictive metrics
    const projectedTotal = isPastMonth ? null : burnRate * daysInMonth;
    const daysToBudget = isPastMonth ? null : (() => {
      const remainingBudget = totalBudget - totalSpent;
      return remainingBudget > 0 && burnRate > 0 ? Math.ceil(remainingBudget / burnRate) : 0;
    })();

    const recommendedDailyLimit = isPastMonth ? null : (() => {
      const daysRemaining = daysInMonth - daysElapsed;
      const remainingBudget = totalBudget - totalSpent;
      return daysRemaining > 0 ? remainingBudget / daysRemaining : 0;
    })();

    return {
      burnRate,
      acceleration,
      projectedTotal,
      daysToBudget,
      recommendedDailyLimit,
      previousBurnRate,
      totalSpent,
      totalBudget,
      daysElapsed,
      isPastMonth
    };
  };

  // Load and analyze month-to-date data
  const loadMTDAnalysis = async (selectedYear: number, selectedMonth: number) => {
    try {
      setLoading(true);
      setError(null);

      const mtdPercentage = calculateMTDPercentage();
      const currentDay = getDate(new Date());
      
      // Get current month data
      const currentMonthBudgets = await fetchBudgetsWithTransactions(selectedYear, selectedMonth);
      setCurrentMonthBudgets(currentMonthBudgets);
      
      // Calculate previous month
      const previousMonth = selectedMonth === 0 ? 11 : selectedMonth - 1;
      const previousYear = selectedMonth === 0 ? selectedYear - 1 : selectedYear;
      
      // Get previous month data
      const previousMonthBudgets = await fetchBudgetsWithTransactions(previousYear, previousMonth);
      
      // Calculate quick stats
      const totalSpent = currentMonthBudgets.reduce((sum, budget) => sum + (budget.spent || 0), 0);
      const totalBudget = currentMonthBudgets.reduce((sum, budget) => sum + (budget.amount || 0), 0);
      const daysInMonth = getDaysInMonth(new Date(selectedYear, selectedMonth));
      const daysElapsed = Math.min(currentDay, daysInMonth);
      const averageDailySpending = daysElapsed > 0 ? totalSpent / daysElapsed : 0;
      const budgetUtilization = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;
      
      setQuickStats({
        totalSpent,
        averageDailySpending,
        totalBudget,
        budgetUtilization,
        daysInMonth,
        daysElapsed
      });

      // Calculate spending velocity metrics
      const velocityMetrics = await calculateSpendingVelocity(currentMonthBudgets, previousMonthBudgets, selectedYear, selectedMonth);

      // Calculate top 3 budgets by amount spent
      const sortedBudgets = currentMonthBudgets
        .map(budget => ({
          id: budget._id,
          name: budget.name,
          icon: budget.icon,
          spent: budget.spent || 0,
          amount: budget.amount || 0
        }))
        .sort((a, b) => b.spent - a.spent)
        .slice(0, 3);

      // Prepare chart data for budget comparison
      const chartData = currentMonthBudgets
        .map(budget => {
          const previousBudget = previousMonthBudgets.find(b => b._id === budget._id);
          const currentSpent = budget.spent || 0;
          const previousSpent = previousBudget ? (previousBudget.spent || 0) : 0;
          const currentAmount = budget.amount || 0;
          const previousAmount = previousBudget ? (previousBudget.amount || 0) : 0;
          
          // Cap percentages at 200% to prevent extreme outliers from skewing the chart
          const currentPercentage = currentAmount > 0 ? Math.min((currentSpent / currentAmount) * 100, 200) : 0;
          const previousPercentage = previousAmount > 0 ? Math.min((previousSpent / previousAmount) * 100, 200) : 0;
          
          return {
            name: budget.name,
            currentMonth: currentPercentage,
            previousMonth: previousPercentage,
            currentMonthDollar: currentSpent,
            previousMonthDollar: previousSpent,
            currentMonthActualPercentage: currentAmount > 0 ? (currentSpent / currentAmount) * 100 : 0,
            previousMonthActualPercentage: previousAmount > 0 ? (previousSpent / previousAmount) * 100 : 0
          };
        })
        .filter(item => item.currentMonth > 0 || item.previousMonth > 0) // Only show budgets with spending
        .sort((a, b) => b.currentMonth - a.currentMonth) // Sort by current month spending
        .slice(0, 10); // Show top 10 budgets to avoid overcrowding

      setChartData(chartData);
      
      // Create analysis for each budget
      const analysis: MTDAnalysis[] = [];
      
      for (const currentBudget of currentMonthBudgets) {
        const previousBudget = previousMonthBudgets.find(b => b._id === currentBudget._id);
        
        // Calculate current month spent (this is already MTD since we're using current month data)
        const currentMonthSpent = currentBudget.spent || 0;
        
        // Calculate previous month spent for the same time period
        let previousMonthSpent = 0;
        if (previousBudget) {
          // For previous month, we need to calculate what was spent up to the same day
          // Since we don't have day-by-day data, we'll estimate based on the percentage
          const previousMonthTotal = previousBudget.spent || 0;
          previousMonthSpent = (previousMonthTotal * mtdPercentage) / 100;
        }
        
        // Calculate percentage change
        let percentageChange = 0;
        if (previousMonthSpent > 0) {
          percentageChange = ((currentMonthSpent - previousMonthSpent) / previousMonthSpent) * 100;
        } else if (currentMonthSpent > 0) {
          percentageChange = 100; // If no previous data but current spending, it's 100% increase
        }
        
        // Only include budgets with significant changes (>15%)
        if (Math.abs(percentageChange) > 15) {
          analysis.push({
            budgetId: currentBudget._id,
            budgetName: currentBudget.name,
            budgetIcon: currentBudget.icon,
            currentMonthSpent,
            previousMonthSpent,
            percentageChange,
            isIncrease: percentageChange > 0
          });
        }
      }
      
      setMtdAnalysis(analysis);

      // Calculate budget statuses
      const canShowData = true;

      let underBudget: BudgetStatus[] = [];
      let overBudget: BudgetStatus[] = [];

      if (canShowData) {
        currentMonthBudgets.forEach(budget => {
          const spent = budget.spent || 0;
          const amount = budget.amount || 0;
          const percentageUsed = amount > 0 ? (spent / amount) * 100 : 0;

          // Under budget: <90% (100% - 10% buffer), Over budget: >105% (100% + 5% threshold)
          const isUnderBudget = percentageUsed < 90;
          const isOverBudget = percentageUsed > 105;

          const budgetStatus: BudgetStatus = {
            budgetId: budget._id,
            budgetName: budget.name,
            budgetIcon: budget.icon,
            amount,
            spent,
            percentageUsed,
            isUnderBudget,
            isOverBudget
          };

          if (isUnderBudget) {
            underBudget.push(budgetStatus);
          } else if (isOverBudget) {
            overBudget.push(budgetStatus);
          }
        });

        // Sort by percentage used (most under budget first, most over budget first)
        underBudget.sort((a, b) => a.percentageUsed - b.percentageUsed);
        overBudget.sort((a, b) => b.percentageUsed - a.percentageUsed);
      }

      setBudgetStatuses({ underBudget, overBudget, canShowData });
      setTopBudgets(sortedBudgets);
      setSpendingVelocity(velocityMetrics);

      // Calculate Budget Health Score
      const healthScoreResult = calculateBudgetHealthScore(currentMonthBudgets);
      setBudgetHealthScore(healthScoreResult);

      // Calculate Optimization Opportunities
      const optimizationResult = calculateOptimizationOpportunities(currentMonthBudgets, previousMonthBudgets);
      setOptimizationOpportunities(optimizationResult);

    } catch (err: any) {
      setError(err.message || 'Failed to load analysis data');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadMTDAnalysis(year, month);
  }, []);

  // Reload data when year or month changes
  useEffect(() => {
    loadMTDAnalysis(year, month);
  }, [year, month]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target.classList.contains('bg-black')) {
        setShowMTDInfo(false);
      }
    };

    if (showMTDInfo) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMTDInfo]);

  return (
    <div className="min-h-screen flex flex-col items-center">
      <div className="w-full max-w-4xl mx-auto flex flex-col min-h-screen h-screen">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between pt-4 pb-2 px-4 mb-4">
          <div className="flex-1 flex flex-col items-center">
            <img
              src="/budgie_logo_sm.png"
              alt="Budgie Logo"
              className="h-10 object-contain mx-auto"
              style={{ maxWidth: 100 }}
            />
          </div>
        </div>

        {/* Dropdowns */}
        <div className="flex-shrink-0 flex gap-2 px-6 mb-6">
          <NativePillSelect
            value={year.toString()}
            options={years.map(y => y.toString())}
            onChange={v => setYear(Number(v))}
          />
          <NativePillSelect
            value={months[month]}
            options={months}
            onChange={v => setMonth(months.indexOf(v))}
          />
        </div>

        {/* Content Area */}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 px-6 pb-28 pt-6 bg-[#F8F9FA] rounded-xl">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <LoadingSpinner size="lg" />
                <p className="text-gray-500 text-sm">Loading analysis...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <p className="text-red-500 text-sm mb-2">{error}</p>
                <button 
                  onClick={() => loadMTDAnalysis(year, month)}
                  className="text-green-600 text-sm font-medium hover:text-green-700"
                >
                  Try again
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Budget Health Score */}
              {budgetHealthScore && (
                <div className="mb-6">
                  <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-6 border border-blue-200">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <div className="text-sm text-gray-600 font-medium">Budget Health Score</div>
                        <button
                          onClick={() => setShowHealthScoreInfo(!showHealthScoreInfo)}
                          className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors border border-gray-300"
                        >
                          <span className="text-gray-700 font-bold text-sm">?</span>
                        </button>
                      </div>

                      {budgetHealthScore.score === 0 && budgetHealthScore.stage === 'insufficient-data' ? (
                        <>
                          <div className="text-2xl font-bold text-gray-400 mb-2">—</div>
                          <div className="text-sm text-gray-600 mb-3">No data available</div>

                          <div className="flex items-center justify-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                            <div className="text-sm font-medium text-gray-700">Insufficient Data</div>
                          </div>
                        </>
                      ) : budgetHealthScore.score === 0 && budgetHealthScore.stage === 'no-valid-budgets' ? (
                        <>
                          <div className="text-2xl font-bold text-gray-400 mb-2">—</div>
                          <div className="text-sm text-gray-600 mb-3">No valid budgets</div>

                          <div className="flex items-center justify-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                            <div className="text-sm font-medium text-gray-700">No Valid Budgets</div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={`text-4xl font-bold mb-2 ${
                            budgetHealthScore.score >= 86 ? 'text-green-600' :
                            budgetHealthScore.score >= 71 ? 'text-green-600' :
                            budgetHealthScore.score >= 51 ? 'text-yellow-600' :
                            budgetHealthScore.score >= 26 ? 'text-orange-600' : 'text-red-600'
                          }`}>
                            {budgetHealthScore.score}
                          </div>
                          <div className="text-sm text-gray-600 mb-3">out of 100</div>

                          {/* Health indicator */}
                          <div className="flex items-center justify-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${
                              budgetHealthScore.score >= 86 ? 'bg-green-500' :
                              budgetHealthScore.score >= 71 ? 'bg-green-500' :
                              budgetHealthScore.score >= 51 ? 'bg-yellow-500' :
                              budgetHealthScore.score >= 26 ? 'bg-orange-500' : 'bg-red-500'
                            }`}></div>
                            <div className={`text-sm font-medium ${
                              budgetHealthScore.score >= 86 ? 'text-green-700' :
                              budgetHealthScore.score >= 71 ? 'text-green-700' :
                              budgetHealthScore.score >= 51 ? 'text-yellow-700' :
                              budgetHealthScore.score >= 26 ? 'text-orange-700' : 'text-red-700'
                            }`}>
                              {budgetHealthScore.score >= 86 ? 'Excellent' :
                               budgetHealthScore.score >= 71 ? 'Good' :
                               budgetHealthScore.score >= 51 ? 'OK' :
                               budgetHealthScore.score >= 26 ? 'Bad' : 'Critical'}
                            </div>
                          </div>

                          {/* Progress bar */}
                          {/* <div className="mt-4 max-w-xs mx-auto">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all duration-500 ${
                                  budgetHealthScore.score >= 86 ? 'bg-green-500' :
                                  budgetHealthScore.score >= 71 ? 'bg-green-500' :
                                  budgetHealthScore.score >= 51 ? 'bg-yellow-500' :
                                  budgetHealthScore.score >= 26 ? 'bg-orange-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${budgetHealthScore.score}%` }}
                              ></div>
                            </div>
                          </div> */}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Health Score Info Dialog */}
              {showHealthScoreInfo && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative">
                    <div className="text-sm text-gray-700">

                      {budgetHealthScore && budgetHealthScore.score > 0 && (
                        <div className="border-b border-gray-200 pb-4">
                          <div className="font-medium text-gray-900 mb-2">Your Current Score Explanation:</div>
                          <div className="text-gray-700 italic">
                            {generateHealthScoreExplanation(currentMonthBudgets, budgetHealthScore.score)}
                          </div>
                        </div>
                      )}

                      <div className="font-semibold text-base mt-4 text-gray-900">How Budget Health Score Works</div>
                      <div className="space-y-3 mt-4">
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-400 mt-2 flex-shrink-0"></div>
                          <div><strong>Base Score:</strong> Each category begins at 100; your score only decreases due to clear issues.</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0"></div>
                          <div><strong>Overspending:</strong> Going over a category’s budget lowers your score in proportion to how far over you are.</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-yellow-400 mt-2 flex-shrink-0"></div>
                          <div><strong>Under‑utilization (Mid‑Month):</strong> If 50%+ through the month and a category is barely used, your score may decrease slightly.</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2 flex-shrink-0"></div>
                          <div><strong>Under‑utilization (Late‑Month):</strong> If 75%+ through the month and a category remains far underused, the penalty is slightly stronger.</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0"></div>
                          <div><strong>No Progress Penalties:</strong> Being close to full budget is never penalized; only true overspending matters.</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2 flex-shrink-0"></div>
                          <div><strong>Worst Budget Impact:</strong> Severely overspent budgets heavily penalize your overall score (worst budget gets 70% weight if ≤20 score).</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <div><strong>Score Range:</strong> 0–100 with color cues to highlight overall health.</div>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowHealthScoreInfo(false)}
                      className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Quick Stats Section */}
              {quickStats && (
                <div className="mb-6">
                  <div className="text-lg font-semibold text-gray-900 mb-3">Quick Stats</div>
                  <div className="grid grid-cols-2 gap-3">
                    <QuickStatsCard
                      title="Total Spent"
                      value={`$${quickStats.totalSpent.toFixed(2)}`}
                      subtitle={`of $${quickStats.totalBudget.toFixed(2)}`}
                      color={quickStats.budgetUtilization === 0 ? "gray" : quickStats.budgetUtilization > 100 ? "red" : quickStats.budgetUtilization > 70 ? "yellow" : "green"}
                    />
                    <QuickStatsCard
                      title="Remaining"
                      value={`$${(quickStats.totalBudget - quickStats.totalSpent).toFixed(2)}`}
                      subtitle={(() => {
                        const now = new Date();
                        const currentYearMonth = now.getFullYear() * 12 + now.getMonth();
                        const viewedYearMonth = year * 12 + month;
                        const isPastMonth = viewedYearMonth < currentYearMonth;

                        if (isPastMonth) {
                          return `${months[month]} ${year}`;
                        } else {
                          return "this month";
                        }
                      })()}
                      color={quickStats.budgetUtilization === 0 ? "gray" : quickStats.budgetUtilization > 100 ? "red" : quickStats.budgetUtilization > 70 ? "yellow" : "green"}
                    />
                    <QuickStatsCard
                      title="Percent Used"
                      value={`${quickStats.budgetUtilization.toFixed(0)}%`}
                      subtitle={(() => {
                        const now = new Date();
                        const currentYearMonth = now.getFullYear() * 12 + now.getMonth();
                        const viewedYearMonth = year * 12 + month;
                        const isPastMonth = viewedYearMonth < currentYearMonth;

                        if (isPastMonth) {
                          return `full month (${quickStats.daysInMonth} days)`;
                        } else {
                          return `${quickStats.daysElapsed}/${quickStats.daysInMonth} days elapsed`;
                        }
                      })()}
                      color={quickStats.budgetUtilization === 0 ? "gray" : quickStats.budgetUtilization > 100 ? "red" : quickStats.budgetUtilization > 70 ? "yellow" : "green"}
                    />
                                         <QuickStatsCard
                       title="Percent Remaining"
                       value={`${(100 - quickStats.budgetUtilization).toFixed(0)}%`}
                       subtitle={(() => {
                         const now = new Date();
                         const currentYearMonth = now.getFullYear() * 12 + now.getMonth();
                         const viewedYearMonth = year * 12 + month;
                         const isPastMonth = viewedYearMonth < currentYearMonth;

                         if (isPastMonth) {
                           return `full month (${quickStats.daysInMonth} days)`;
                         } else {
                           return `${quickStats.daysElapsed}/${quickStats.daysInMonth} days elapsed`;
                         }
                       })()}
                       color={quickStats.budgetUtilization === 0 ? "gray" : quickStats.budgetUtilization > 100 ? "red" : quickStats.budgetUtilization > 70 ? "yellow" : "green"}
                     />
                  </div>
                </div>
              )}

              {/* Spending Velocity Section */}
              {spendingVelocity && (
                <div className="mb-6">
                  <div className="text-lg font-semibold text-gray-900 mb-3">Spending Velocity</div>

                  {/* Two-column Burn Rate and Acceleration Card */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 text-center">
                        <div className="text-sm text-gray-500 font-medium mb-2">Burn Rate</div>
                        <div className="text-2xl font-bold text-gray-900 mb-1">
                          ${spendingVelocity.burnRate.toFixed(2)}/day
                        </div>
                                          <div className="text-sm text-gray-600">
                    over {spendingVelocity.isPastMonth ? 'total' : 'elapsed'} {spendingVelocity.daysElapsed} day{spendingVelocity.daysElapsed !== 1 ? 's' : ''}
                  </div>
                      </div>

                      <div className="flex-1 text-center border-l border-gray-200 pl-6">
                        <div className="text-sm text-gray-500 font-medium mb-2">Acceleration</div>
                        <div className={`text-2xl font-bold ${spendingVelocity.acceleration > 0 ? 'text-red-600' : spendingVelocity.acceleration < 0 ? 'text-green-600' : 'text-gray-900'}`}>
                          {spendingVelocity.acceleration > 0 ? '+' : ''}{spendingVelocity.acceleration.toFixed(0)}%
                        </div>
                        <div className="text-sm text-gray-600">
                          {spendingVelocity.acceleration > 0 ? 'Increasing' : spendingVelocity.acceleration < 0 ? 'Decreasing' : 'Stable'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Three Metric Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Daily Limit Card */}
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                      <div className="flex flex-col">
                        <div className="text-sm text-gray-500 font-medium mb-2">Daily Limit to Stay on Budget</div>
                        <div className="text-xl font-bold text-gray-900">
                          {spendingVelocity.recommendedDailyLimit !== null ? `$${spendingVelocity.recommendedDailyLimit.toFixed(2)}` : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {spendingVelocity.isPastMonth ? 'Past month data' : `at current burn rate`}
                        </div>
                      </div>
                    </div>

                    {/* Days to Budget Card */}
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                      <div className="flex flex-col">
                        <div className="text-sm text-gray-500 font-medium mb-2">Days to Reach Total Budget</div>
                        <div className="text-xl font-bold text-gray-900">
                          {spendingVelocity.daysToBudget !== null ? spendingVelocity.daysToBudget : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {spendingVelocity.isPastMonth ? 'Past month data' : 'at current burn rate'}
                        </div>
                      </div>
                    </div>

                    {/* Projected Total Card */}
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                      <div className="flex flex-col">
                        <div className="text-sm text-gray-500 font-medium mb-2">Projected Total Spent</div>
                        <div className="text-xl font-bold text-gray-900">
                          {spendingVelocity.projectedTotal !== null ? `$${spendingVelocity.projectedTotal.toFixed(2)}` : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {spendingVelocity.isPastMonth ? 'Past month data' : 'by month end'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Top 3 Budgets Section */}
              {topBudgets.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-lg font-semibold text-gray-900">Top 3 Budgets by Spending</div>
                  </div>
                  
                  <div className="space-y-3">
                    {topBudgets.map((budget, index) => {
                      // Render icon - handle both Font Awesome classes and emoji
                      const renderIcon = () => {
                        if (budget.icon.startsWith('fas fa-') || budget.icon.startsWith('far fa-') || budget.icon.startsWith('fab fa-')) {
                          return <i className={budget.icon}></i>;
                        }
                        return budget.icon;
                      };

                      const getRankColor = () => {
                        switch (index) {
                          // case 0: return 'text-red-600 bg-red-100';
                          // case 1: return 'text-orange-600 bg-orange-100';
                          // case 2: return 'text-yellow-600 bg-yellow-100';
                          default: return 'text-grey-700 bg-emerald-50';
                        }
                      };

                      return (
                        <div key={budget.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {/* Show rank number only on larger screens */}
                              <div className={`hidden md:flex items-center justify-center w-8 h-8 rounded-full ${getRankColor()}`}>
                                <span className="text-sm font-bold">{index + 1}</span>
                              </div>
                              {/* Smaller icon on mobile, normal size on desktop */}
                              <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-[#F8F9FA]">
                                <div className="text-sm md:text-base">{renderIcon()}</div>
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">{budget.name}</div>
                                <div className="text-sm text-gray-600">
                                  ${budget.spent.toFixed(2)} of ${budget.amount.toFixed(2)}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-900">
                                ${budget.spent.toFixed(2)}
                              </div>
                              <div className="text-sm text-gray-500">
                                {(budget.amount > 0 ? (budget.spent / budget.amount) * 100 : 0).toFixed(0)}% of budget
                              </div>
                              <div className="text-sm font-semibold text-blue-600">
                                {(spendingVelocity?.totalSpent > 0 ? (budget.spent / spendingVelocity.totalSpent) * 100 : 0).toFixed(1)}% of total
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Budget Comparison Chart */}
              {chartData.length > 0 && (
                <div className="mb-6">
                  <div className="text-lg font-semibold text-gray-900 mb-3">Budget Spending Comparison (top 10)</div>
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="name" 
                            stroke="#666"
                            fontSize={12}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                                                  <YAxis 
                          stroke="#666" 
                          fontSize={12}
                          tickFormatter={(value: any) => `${value.toFixed(0)}%`}
                          domain={[0, (dataMax: number) => Math.min(dataMax * 1.1, 200)]}
                        />
                                                    <Tooltip 
                            formatter={(value: any, name: string, props: any) => {
                              const budget = props.payload;
                              const dollarAmount = name === 'currentMonth' ? budget?.currentMonthDollar : budget?.previousMonthDollar;
                              const actualPercentage = name === 'currentMonth' ? budget?.currentMonthActualPercentage : budget?.previousMonthActualPercentage;
                              return [
                                `${actualPercentage?.toFixed(1) || '0.0'}% ($${dollarAmount?.toFixed(2) || '0.00'})`, 
                                name === 'currentMonth' ? 'This Month' : 'Last Month'
                              ];
                            }}
                            labelStyle={{ color: '#374151' }}
                            contentStyle={{
                              backgroundColor: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            }}
                          />
                          <Legend 
                            formatter={(value: any) => value === 'currentMonth' ? 'This Month' : 'Last Month'}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="currentMonth" 
                            stroke="#10b981" 
                            strokeWidth={3}
                            dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="previousMonth" 
                            stroke="#6b7280" 
                            strokeWidth={3}
                            dot={{ fill: '#6b7280', strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, stroke: '#6b7280', strokeWidth: 2 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {/* Budget Status Section */}
              <div className="">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-lg font-semibold text-gray-900">Budget Status</div>
                </div>

                {!budgetStatuses.canShowData ? (
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="text-center">
                      <div className="text-gray-400 mb-2">
                        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="text-gray-600 font-medium mb-1">Budget Status Unavailable</p>
                      <p className="text-gray-500 text-sm">
                        This data is only available after the selected month has ended. Please select a past month to view budget status.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {/* Over Budget Section */}
                    {budgetStatuses.overBudget.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <div className="text-base font-semibold text-gray-900">Over Budget (&gt;105% used)</div>
                        </div>
                        <div className="space-y-3">
                          {budgetStatuses.overBudget.map(budget => (
                            <BudgetStatusItem key={budget.budgetId} budget={budget} />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Under Budget Section */}
                    {budgetStatuses.underBudget.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          <div className="text-base font-semibold text-gray-900">Under Budget (&lt;90% used)</div>
                        </div>
                        <div className="space-y-3">
                          {budgetStatuses.underBudget.map(budget => (
                            <BudgetStatusItem key={budget.budgetId} budget={budget} />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Empty State */}
                    {budgetStatuses.underBudget.length === 0 && budgetStatuses.overBudget.length === 0 && (
                                              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                          <div className="text-center">
                            <p className="text-gray-500 text-sm">No extreme budget variances</p>
                            <p className="text-gray-400 text-xs">All budgets are between 90-105% utilization</p>
                          </div>
                        </div>
                    )}
                  </div>
                )}
              </div>

              {/* Optimization Opportunities Section */}
              <div className="mt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-lg font-semibold text-gray-900">Optimization Opportunities</div>
                </div>

                {!optimizationOpportunities.canShowData ? (
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="text-center">
                      <div className="text-gray-400 mb-2">
                        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <p className="text-gray-600 font-medium mb-1">No Optimization Opportunities</p>
                      <p className="text-gray-500 text-sm">
                        All budgets are being utilized efficiently. No significant underutilization detected over the past two months.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Total Savings Summary */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-lg font-semibold text-gray-900 mb-1">Total Optimization Potential</div>
                          <div className="text-sm text-gray-600">
                            Reduce {optimizationOpportunities.opportunities.length} budget{optimizationOpportunities.opportunities.length !== 1 ? 's' : ''} by 20% each
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-blue-600">
                            ${optimizationOpportunities.totalSavings.toFixed(2)}
                          </div>
                          <div className="text-sm text-gray-600">available for reallocation</div>
                        </div>
                      </div>
                    </div>

                    {/* Individual Opportunities */}
                    <div className="space-y-3">
                      {optimizationOpportunities.opportunities.map((opportunity, index) => (
                        <div key={opportunity.budgetId} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                                  <span className="text-orange-600 font-bold text-sm">-</span>
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-900">{opportunity.budgetName}</div>
                                  <div className="text-sm text-gray-600">
                                    Consistently underutilized: {opportunity.currentUtilization.toFixed(0)}% this month, {opportunity.previousUtilization.toFixed(0)}% last month
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-900 mb-1">
                                -${opportunity.reductionAmount.toFixed(2)}
                              </div>
                              <div className="text-sm text-gray-600">
                                ${opportunity.currentAmount.toFixed(2)} → ${opportunity.suggestedAmount.toFixed(2)}
                              </div>
                              <div className="text-xs text-orange-600 font-medium">
                                20% reduction
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Recommendation */}
                    <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <div className="font-semibold text-green-800 mb-1">Recommendation</div>
                          <div className="text-sm text-green-700">
                            Consider reallocating the ${optimizationOpportunities.totalSavings.toFixed(2)} in savings to budgets that are consistently overutilized or to build emergency savings.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* MTD Analysis Section */}
              <div className="flex items-center gap-3 mb-2 mt-8">
                <div className="text-lg font-semibold text-gray-900">Month-to-Date Analysis</div>
                <div className="relative mtd-info-popover">
                  <button
                    onClick={() => setShowMTDInfo(!showMTDInfo)}
                    className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors border border-gray-300"
                  >
                    <span className="text-gray-700 font-bold text-lg">?</span>
                  </button>
                </div>
              </div>
              
              {/* Overlay Popover */}
              {showMTDInfo && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 relative">
                    <div className="text-sm text-gray-700">
                      <div className="font-semibold text-base mb-3 text-gray-900">How Month-to-Date Analysis Works</div>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <div>Calculates how far through the current month we are.</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <div>Compares current spending to the same period in the previous month.</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <div>Shows budgets with &gt;15% change.</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <div>Red = spending more, Green = spending less.</div>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowMTDInfo(false)}
                      className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              {mtdAnalysis.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <p className="text-gray-500 text-sm mb-2">No significant changes found</p>
                    <p className="text-gray-400 text-xs">Budgets with &gt;15% change will appear here</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {mtdAnalysis.map(analysis => (
                    <MTDAnalysisItem key={analysis.budgetId} analysis={analysis} />
                  ))}
                </div>
              )}

            </>
          )}
        </div>
      </div>
    </div>
  );
} 